import { useEffect, useState } from "react";

const NAV_ITEMS = [
  { id: "profile", label: "影片档案" },
  { id: "plot", label: "剧情详解" },
  { id: "characters", label: "主要角色" },
  { id: "cast", label: "演员表" },
  { id: "commentary", label: "分幕解说" },
  { id: "themes", label: "主题解析" },
  { id: "reviews", label: "影迷短评" },
  { id: "faq", label: "常见问题" },
];

const UPDATES = [
  "新增 6 张核心角色概念图",
  "补充第三幕和解段落逐场解说",
  "收录 5 条影迷短评并按维度分类",
  "整理 5 个高频常见问题",
  "更新影片基本信息与演员表档案",
];

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("profile");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_ITEMS.map((item) => document.getElementById(item.id)).filter(
      (el): el is HTMLElement => Boolean(el)
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMenuOpen(false);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="bg-foreground-950 text-background-100">
        <div className="flex items-center gap-3 px-4 md:px-6 h-9 overflow-hidden">
          <span className="shrink-0 inline-flex items-center gap-1.5 font-label text-[11px] tracking-wider text-primary-300">
            <i className="ri-flashlight-fill" aria-hidden="true" />
            更新
          </span>
          <div className="flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee">
              {[...UPDATES, ...UPDATES].map((item, index) => (
                <span
                  key={`${item}-${index}`}
                  className="shrink-0 px-5 text-[12px] text-background-200/90 whitespace-nowrap"
                >
                  <i className="ri-record-circle-line mr-1.5 text-primary-400" aria-hidden="true" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <nav
        className={`transition-colors duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur border-b border-background-200"
            : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between gap-4 px-4 md:px-6 h-16">
          <button
            type="button"
            onClick={() => scrollTo("top")}
            className="flex items-center gap-2.5 cursor-pointer"
            aria-label="回到页面顶部"
          >
            <span
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg ${
                scrolled
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50/90 text-primary-600"
              }`}
            >
              <i className="ri-quill-pen-fill" aria-hidden="true" />
            </span>
            <span
              className={`font-heading text-lg font-bold tracking-tight ${
                scrolled ? "text-foreground-950" : "text-background-50"
              }`}
            >
              漂亮的小姨子
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  scrollTo(item.id);
                }}
                className={`px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                  active === item.id
                    ? "text-primary-600 bg-primary-100"
                    : scrolled
                      ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                      : "text-background-100/90 hover:text-background-50 hover:bg-background-50/15"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <a
              href="#commentary"
              onClick={(event) => {
                event.preventDefault();
                scrollTo("commentary");
              }}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-play-circle-line text-base" aria-hidden="true" />
              看分幕解说
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className={`lg:hidden w-10 h-10 rounded-md flex items-center justify-center text-xl cursor-pointer ${
                scrolled ? "text-foreground-900 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
              }`}
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true" />
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4 grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  scrollTo(item.id);
                }}
                className={`px-3 py-2.5 rounded-md text-sm ${
                  active === item.id
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
        ) : null}
      </nav>
    </header>
  );
}